/**
 * A/B testing framework — reporting half.
 *
 * Bucket assignment happens inline in snippets/ab-tests.liquid (so it runs
 * before variant-gated markup renders); this deferred script handles the
 * reporting duties:
 *
 * 1. Pageview beacon — once per session per path per running test, carrying an
 *    anonymous session id and the session's viewport band. The dashboard
 *    derives exposure (first row for a session) and any funnel goal from
 *    these, so goals can be defined after the data is collected.
 * 2. Step beacon — for single-URL flows that declare their current step in a
 *    `data-ab-step` attribute, and for surfaces that report an engagement step
 *    on the `edz:ab:step` event. Path alone can't describe either.
 * 3. Legacy exposure + goal beacons — the pre-pageview format, kept until the
 *    dashboard's own goal definitions are verified against them. Deliberately
 *    carry no session id: that field is what tells the two formats apart.
 * 4. Order attribution — stamp `ab_<handle>: <bucket>` cart attributes so
 *    every order records which buckets its buyer was in. Attributes ride the
 *    cart into the order as note attributes.
 */

(() => {
  const buckets = window.EdzAB?.buckets;
  if (!buckets || Object.keys(buckets).length === 0) return;

  const configEl = document.getElementById('ab-tests-config');
  let config = {};
  try {
    config = JSON.parse(configEl.textContent) || {};
  } catch {
    /* config unreadable — attribution still works */
  }
  // Set by the inline snippet: handles whose bucket came from an ?ab_<handle>=
  // URL parameter rather than from randomization. Rides every beacon so the
  // dashboard can exclude QA and staff traffic from results.
  const forcedHandles = window.EdzABForced || {};

  // Dark-launch fields, set by the inline snippet: a hashed, durable visitor
  // id derived from Shopify's own `_shopify_y` token, and the bucket that hash
  // WOULD assign for each test. Neither drives assignment while the visitor
  // bucketing setting is off — they ride the pageview/step stream so the split
  // can be checked against real traffic before it decides anything. Absent for
  // a visitor who declined tracking consent (no `_shopify_y` to hash), and
  // both fields are then omitted rather than sent empty.
  const visitorId = window.EdzAB?.visitorId || null;
  const hashBuckets = window.EdzAB?.hashBuckets || {};

  const beaconUrl = config.beacon || null;
  const themeId = config.theme ?? null;
  const country = config.country || null;
  // "main" on the live theme, "development"/"unpublished" elsewhere. Sent so
  // the dashboard can exclude every non-live theme by rule rather than by
  // maintaining a list of preview theme ids — a new dev theme or a CLI dev
  // server would otherwise count as live traffic.
  const role = config.role || null;
  const tests = Array.isArray(config.tests) ? config.tests : [];

  // text/plain keeps the cross-origin POST a "simple" request, so the browser
  // never needs a preflight — which sendBeacon cannot perform.
  const beacon = (payload) =>
    navigator.sendBeacon(beaconUrl, new Blob([JSON.stringify(payload)], { type: 'text/plain' }));

  // Trailing slashes are cosmetic in Shopify routing; "/" must stay "/".
  const normalizePath = (path) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

  // Query strings are stripped before sending: ad and share parameters would
  // otherwise make path cardinality unbounded and split one page's rows.
  const currentPath = () => normalizePath(location.pathname);

  const claimOnce = (key) => {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
      return true;
    } catch {
      return false; // storage blocked — skip rather than beacon every pageview
    }
  };

  // --- Session id ----------------------------------------------------------

  // Anonymous, per browsing session, shared across every concurrent test — one
  // session in two tests carries one id, which is what lets the dashboard ask
  // whether two live tests interacted. Never joined to a customer, and never
  // reused as a bucket cookie. A malformed value is dropped by the endpoint
  // rather than demoted to a legacy row, so the fallback must be a real v4.
  const uuid = () => {
    if (crypto.randomUUID) return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };

  // The endpoint drops a malformed session outright rather than demoting it to
  // a legacy row — deliberately, so a broken new-format beacon can't hide
  // inside a real number. Validate here too: a stored value we can't vouch for
  // is replaced rather than sent.
  const SESSION_SHAPE = /^[A-Za-z0-9_-]{8,64}$/;

  // Read at activation, not parse time — a prerendered document has its own
  // fresh sessionStorage, so reading here inside the prerender would mint an id
  // the visible page inherits and phantom-count the never-clicked prerender.
  // See the init note at the foot of the file. `let`, assigned by `start()`.
  let sessionId = null;
  const readSessionId = () => {
    try {
      const existing = sessionStorage.getItem('edz_ab_session');
      if (existing && SESSION_SHAPE.test(existing)) return existing;
      const fresh = uuid();
      sessionStorage.setItem('edz_ab_session', fresh);
      return SESSION_SHAPE.test(fresh) ? fresh : null;
    } catch {
      return null; // storage blocked — send legacy beacons only
    }
  };

  // --- Viewport band -------------------------------------------------------

  // Which width band the session opened in. Carried so a result can be read on
  // one band alone: arms routinely differ by breakpoint, and an engagement
  // metric is only comparable across arms on the widths where both arms render
  // the same thing. `newnav` is the case in hand — identical closed headers
  // below 750px, a folded bar between 750px and 1350px.
  //
  // Bands, not a device type. What changes the rendering is the viewport
  // width, not the hardware, and a UA sniff answers the other question — a
  // phone in landscape and a small desktop window render identically and must
  // land in the same band. Named after the band's lower bound so that moving a
  // breakpoint mints a new token instead of quietly changing what an old one
  // meant.
  //
  // Measured with matchMedia rather than innerWidth: a classic desktop
  // scrollbar counts towards innerWidth and not towards the `width` media
  // feature, so the two disagree by ~15px and a window sitting on a boundary
  // is filed differently depending which one you ask. matchMedia is the same
  // question the stylesheet asks, so the band and the CSS can never disagree
  // about what was on screen.
  //
  // `min-width` rather than range syntax. A browser that can't parse
  // `(width < 750px)` reports `matches: false` rather than throwing, which
  // would file every phone in the widest band — the failure mode is silent and
  // points the wrong way.
  const DEVICE_SHAPE = /^w(0|750|1350)$/;

  const measureBand = () => {
    if (!matchMedia('(min-width: 750px)').matches) return 'w0';
    return matchMedia('(min-width: 1350px)').matches ? 'w1350' : 'w750';
  };

  // Sticky for the session, like the session id, so every row of a session
  // agrees on one band and the dashboard never has to pick which row defines
  // it. Accepted cost: a tablet rotated across 750px mid-session keeps the
  // band it started in.
  //
  // Also read at activation, not parse time: a prerender is laid out at its own
  // size, so measuring here would file the band of the hidden document rather
  // than the one the visitor actually sees. `let`, assigned by `start()`.
  let device = null;
  const readDevice = () => {
    try {
      const existing = sessionStorage.getItem('edz_ab_device');
      if (existing && DEVICE_SHAPE.test(existing)) return existing;
      const fresh = measureBand();
      sessionStorage.setItem('edz_ab_device', fresh);
      return fresh;
    } catch {
      return measureBand(); // storage blocked — no event beacons go out anyway
    }
  };

  // --- Volume guards -------------------------------------------------------

  // Backstop against a redirect loop or a flow that thrashes its step
  // attribute. The dashboard can see sessions that hit the cap (they sit at
  // exactly MAX_EVENTS rows) and treats their misses as unknown, so don't
  // lower this without telling them.
  const MAX_EVENTS = 30;
  // Re-entries past this saturate — a shopper toggling endlessly shouldn't
  // dominate the counts.
  const MAX_REENTRIES = 5;

  const counterKey = (handle) => `edz_ab_n_${handle}`;

  const underCap = (handle) => {
    try {
      const n = Number(sessionStorage.getItem(counterKey(handle)) || 0);
      if (n >= MAX_EVENTS) return false;
      sessionStorage.setItem(counterKey(handle), String(n + 1));
      return true;
    } catch {
      return false;
    }
  };

  // --- 1. Pageview beacons -------------------------------------------------

  const sendEvent = (handle, bucket, extra = {}) => {
    if (!beaconUrl || !sessionId) return;
    if (!underCap(handle)) return;
    beacon({
      test: handle,
      bucket,
      path: currentPath(),
      session: sessionId,
      country,
      device,
      theme: themeId,
      role,
      ts: Date.now(),
      ...(forcedHandles[handle] ? { forced: true } : {}),
      ...(visitorId ? { vid: visitorId } : {}),
      ...(visitorId && hashBuckets[handle] ? { vbucket: hashBuckets[handle] } : {}),
      ...extra,
    });
  };

  const sendPageviews = () => {
    const path = currentPath();
    for (const [handle, bucket] of Object.entries(buckets)) {
      if (!claimOnce(`edz_ab_pv_${handle}_${path}`)) continue;
      sendEvent(handle, bucket);
    }
  };

  // --- 2. Step beacons -----------------------------------------------------

  // Re-entering a step is signal, not noise: on the subscribe flow, going back
  // to add another child is the multiplan challenger working. Every view is
  // sent with first_reach saying which kind it is, rather than deduping the
  // repeats away.
  // The endpoint drops a malformed step rather than filing it as a pageview,
  // which would double-count the path. Normalize to its shape here so a flow
  // that names a step loosely loses that one step instead of the beacon.
  const STEP_SHAPE = /^[a-z0-9_-]{1,32}$/;

  const sendStep = (raw) => {
    const step = String(raw ?? '')
      .trim()
      .toLowerCase();
    if (!STEP_SHAPE.test(step)) return;
    for (const [handle, bucket] of Object.entries(buckets)) {
      const firstKey = `edz_ab_step_${handle}_${step}`;
      const first = claimOnce(firstKey);
      if (!first) {
        const reKey = `edz_ab_re_${handle}_${step}`;
        try {
          const n = Number(sessionStorage.getItem(reKey) || 0);
          if (n >= MAX_REENTRIES) continue;
          sessionStorage.setItem(reKey, String(n + 1));
        } catch {
          continue;
        }
      }
      sendEvent(handle, bucket, { step, first_reach: first });
    }
  };

  const watchStepHost = (host) => {
    // Read on attach, not only on mutation. The flow renders the entry step
    // server-side and its JS only writes when the step *changes*, so a session
    // that never advances mutates nothing — observing mutations alone would
    // miss the one step every session reaches.
    sendStep(host.getAttribute('data-ab-step'));

    new MutationObserver(() => sendStep(host.getAttribute('data-ab-step'))).observe(host, {
      attributes: true,
      attributeFilter: ['data-ab-step'],
    });
  };

  const watchSteps = () => {
    // Every host, not the first one in the document. This was a `querySelector`
    // while the subscribe flow was the only element declaring a step, which
    // made the mechanism first-come-first-served: anything in the header —
    // parsed before every section — would have taken step tracking away from
    // the flow and silently emptied its funnel.
    for (const host of document.querySelectorAll('[data-ab-step]')) watchStepHost(host);

    // The event path, for interactions a surface has no state to declare. A
    // drawer that opens and closes leaves no attribute behind, and giving the
    // header a `data-ab-step` would add a step host to every page in the theme
    // just to report one interaction. The feature still only says what
    // happened; the framework owns the dedupe, the payload and the transport,
    // exactly as it does for the attribute.
    //
    // Engagement names are namespaced (`nav_*`) and flow steps stay bare.
    // `sendStep` records a step against every test the visitor is bucketed
    // into, so a header interaction lands on the subscribe flow's tests too —
    // the prefix is what lets the dashboard keep it out of their funnels.
    document.addEventListener('edz:ab:step', (event) => sendStep(event.detail?.step));
  };

  // --- 3. Legacy exposure + goal beacons -----------------------------------

  // No session field by design — its presence is how the endpoint tells the
  // new format from the old. Both run until the dashboard's own goal
  // definitions are verified against these numbers, then these go.
  //
  // Forced handles are skipped entirely rather than flagged: the legacy table
  // has no `forced` column, so a flag would be ignored and QA traffic would
  // land in the stream the current results still come from. Both streams
  // therefore exclude forced sessions, which keeps them comparable.

  const sendBeacons = () => {
    if (!beaconUrl) return;
    for (const [handle, bucket] of Object.entries(buckets)) {
      if (forcedHandles[handle]) continue;
      if (!claimOnce(`edz_ab_seen_${handle}`)) continue;
      beacon({
        test: handle,
        bucket,
        path: location.pathname,
        theme: themeId,
        ts: Date.now(),
      });
    }
  };

  const sendGoalBeacons = () => {
    if (!beaconUrl) return;
    const here = currentPath();
    for (const test of tests) {
      const bucket = buckets[test.handle];
      if (!test.enabled || !bucket) continue;
      if (forcedHandles[test.handle]) continue;
      const goal = (test.goal || '').trim();
      if (!goal || normalizePath(goal) !== here) continue;
      if (!claimOnce(`edz_ab_goal_${test.handle}`)) continue;
      beacon({
        test: test.handle,
        bucket,
        goal: test.goal,
        theme: themeId,
        ts: Date.now(),
      });
    }
  };

  // --- 4. Cart attribute stamping -------------------------------------------

  // A forced session still gets real cart attributes and can still check out,
  // so an order placed during QA is indistinguishable from a real one unless
  // the cart says so. `_ab_forced` marks it; the underscore keeps it private,
  // as with `_promo_bucket`. Cleared (empty = removed) when nothing is forced,
  // so a session that resets doesn't carry a stale flag to checkout.
  const forcedFlag = Object.keys(forcedHandles).length > 0 ? 'true' : '';

  const signature = Object.entries(buckets)
    .map(([handle, bucket]) => `${handle}:${bucket}`)
    .sort()
    .concat(forcedFlag ? ['_forced'] : [])
    .join('|');

  let syncing = false;

  const syncCartAttributes = async () => {
    if (syncing) return;
    const key = 'edz_ab_cart_stamped';
    try {
      if (sessionStorage.getItem(key) === signature) return;
    } catch {
      /* fall through — worst case is a redundant sync */
    }
    syncing = true;
    try {
      const cart = await fetch('/cart.js').then((response) => response.json());
      if (cart.item_count === 0) return; // nothing to attribute yet; cart:update will retry

      const attributes = { _ab_forced: forcedFlag };
      let stale = (cart.attributes?._ab_forced || '') !== forcedFlag;
      for (const [handle, bucket] of Object.entries(buckets)) {
        attributes[`ab_${handle}`] = bucket;
        if (cart.attributes?.[`ab_${handle}`] !== bucket) stale = true;
      }
      if (stale) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        });
      }
      try {
        sessionStorage.setItem(key, signature);
      } catch {
        /* ignore */
      }
    } catch {
      /* network hiccup — next cart:update retries */
    } finally {
      syncing = false;
    }
  };

  // --- Init ----------------------------------------------------------------

  // Every exposure/step/legacy beacon fires from here, so it can be held until
  // the page is actually shown to someone.
  //
  // Speculative prerender is why. Chrome's Speculation Rules (and a same-origin
  // `<link rel=prerender>`) load /pages/subscribe in full in a hidden document
  // that has its OWN fresh sessionStorage but SHARES the bucket cookie. This
  // deferred script ran there too: it minted a brand-new session id, fired a
  // pageview and the entry-step beacon, and was then discarded if the visitor
  // never clicked through. Those phantom sessions land challenger-heavy — the
  // arm that gets prerendered more often — and were inflating every test's
  // exposure denominator without ever converting. Proof was in the shape: 971
  // of 972 of one arm's two-beacon subscribe-lander sessions resolved in under
  // two seconds, and converting-customer counts matched across arms despite a
  // ~420-session gap.
  //
  // `document.prerendering` is true only inside an active prerender, so on
  // every normal load this branch is inert and `start()` runs synchronously.
  // That is not dead code — do not remove the guard. The session id and
  // viewport band are read inside `start()` (not at parse time) so an activated
  // prerender takes the id it will keep and measures the viewport the visitor
  // actually sees.
  const start = () => {
    sessionId = readSessionId();
    device = readDevice();
    sendPageviews();
    watchSteps();
    sendBeacons();
    sendGoalBeacons();
  };

  if (document.prerendering) {
    document.addEventListener('prerenderingchange', start, { once: true });
  } else {
    start();
  }

  // Not prerender-gated: cart stamping only writes when a cart already exists,
  // and a prerender carrying a cart is a real returning session, not a phantom.
  syncCartAttributes();
  document.addEventListener('cart:update', () => {
    try {
      sessionStorage.removeItem('edz_ab_cart_stamped');
    } catch {
      /* ignore */
    }
    syncCartAttributes();
  });
})();
